export const VIEW_MODE_STORAGE_KEY = 'forpy_module_view_modes'

export const VIEW_MODE_MODULES = {
  projects: 'projects',
  technicalSheets: 'technical_sheets',
  clients: 'clients',
  productionLots: 'traceability_lots',
  farms: 'traceability_farms',
  productionItems: 'traceability_items',
  traceabilityPublicPages: 'traceability_public_pages',
  qualityChecklists: 'quality_checklists'
} as const

export const TABLE_CARDS_VIEW_MODE_VALUES = ['table', 'cards'] as const

export type TableCardsViewMode = (typeof TABLE_CARDS_VIEW_MODE_VALUES)[number]

export type ViewModeModuleId =
  | (typeof VIEW_MODE_MODULES)[keyof typeof VIEW_MODE_MODULES]
  | (string & {})

type ViewModeStore = Record<string, string>

function isPlainObject (value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function normalizeStore (parsed: unknown): ViewModeStore {
  if (!isPlainObject(parsed)) return {}

  const entries = Object.entries(parsed)
  if (entries.every(([, value]) => typeof value === 'string')) {
    return parsed as ViewModeStore
  }

  const merged: ViewModeStore = {}
  entries.forEach(([, scope]) => {
    if (!isPlainObject(scope)) return
    Object.entries(scope).forEach(([moduleId, value]) => {
      if (typeof value === 'string') merged[moduleId] = value
    })
  })
  return merged
}

function readStore (): ViewModeStore {
  if (typeof window === 'undefined') return {}

  try {
    const raw = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY)
    if (!raw) return {}
    return normalizeStore(JSON.parse(raw))
  } catch {
    return {}
  }
}

function writeStore (store: ViewModeStore): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, JSON.stringify(store))
  } catch {
  }
}

export function isAllowedViewMode<T extends string> (
  value: string,
  allowedValues: readonly T[]
): value is T {
  return (allowedValues as readonly string[]).includes(value)
}

export function getModuleViewMode<T extends string> (
  moduleId: ViewModeModuleId,
  allowedValues: readonly T[],
  defaultValue: T
): T {
  const stored = readStore()[moduleId]
  if (typeof stored === 'string' && isAllowedViewMode(stored, allowedValues)) {
    return stored
  }
  return defaultValue
}

export function setModuleViewMode (
  moduleId: ViewModeModuleId,
  value: string
): void {
  writeStore({
    ...readStore(),
    [moduleId]: value
  })
}
